import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Avatar, Box, Card, CardHeader, Stack, Divider } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/system/Unstable_Grid';

import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { isOrganisationTenant } from 'src/services/auth/auth.utils';
import {
  useGetCompanyApplicationsQuery,
  useGetCompanyByIdQuery,
  useGetCompanyContactLinksQuery,
  useGetCompanyJobsQuery
} from 'src/services/connections/connections.service';
import { JobFilters } from 'src/services/jobs/jobs.types';

import ApplicationsTable from 'src/components/custom-tables/applications-table/applications-table';
import Label from 'src/components/label';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { CompanyProfileSection } from 'src/sections/companies/detail/sections/profile-section';

import { TenantType } from 'src/types/enums';

import { rootApi } from 'src/services/rootApi';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import ConnectionJobsTable from '../../connections/detail/connection-jobs-table';
import { CompanyProfileTypeIcon } from '../icons/company-profile-type.icon';
import ContactLinksTable from '../table/contact-links/contact-links-table';
import CompanyDocumentsView from './sections/company-documents';
import { CompanyDetailSection } from './sections/detail-section';
import AccountOwnerCard from './sections/account-owner-card';
import PrimaryContactCard from './sections/primary-contact-card';
import CompanyLinksCard from './sections/company-links-card';
import { CompanyLabelsCard } from './sections/company-labels-card';

// ----------------------------------------------------------------------

export const JOB_PUBLISH_OPTIONS = [
  {
    value: 'published',
    label: 'Published',
  },
  {
    value: 'draft',
    label: 'Draft',
  },
];

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum CompaniesTab {
  DETAIL = 'detail',
  PROFILE = 'profile',
  JOBS = 'jobs',
  APPLICATIONS = 'applications',
  CONTACTS = 'contacts',
  DOCUMENTS = 'documents',
}

export default function MyCompaniesDetailView() {
  const showTable = useBoolean();

  const { t } = useTranslate();

  const { connection_id } = useParams();

  const dispatch = useAppDispatch();

  const [jobsPageOptions, setJobsPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const [applicationsPageOptions, setApplicationsPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const [contactsPageOptions, setContactsPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const [currentTab, setCurrentTab] = useState<CompaniesTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const router = useRouter();

  const tenantType = useOrgTenant();

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: CompaniesTab = CompaniesTab.DETAIL

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(CompaniesTab).includes(existingTab as CompaniesTab)) {
      defaultTab = existingTab as CompaniesTab;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, tenantType, searchParams]);

  useEffect(() => {
    if (!tenantType) return;

    if (tenantType === TenantType.Candidate) {
      router.push(paths.dashboard.root);
    }
  }, [router, tenantType]);

  const { currentData: connectionProfile } = useGetCompanyByIdQuery({
    profile_id: connection_id as string
  });

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: CompaniesTab) => {
    setCurrentTab(newValue);
  }, []);

  const [filters, setFilters] = useState<Omit<JobFilters, 'page' | 'per_page'>>({
    search_query: undefined,
    job_status: undefined, // TODO
    assigned_account: undefined,
  });

  const { currentData: connectionJobs } = useGetCompanyJobsQuery(
    {
      params: { ...jobsPageOptions, ...filters },
      profile_id: connection_id as string
    },
    { skip: !connection_id }
  );

  const { currentData: connectionApplications, isLoading: isLoadingApplications } = useGetCompanyApplicationsQuery({
    params: applicationsPageOptions,
    profile_id: connection_id as string
  });

  const { currentData: contactLinks } = useGetCompanyContactLinksQuery({
    params: contactsPageOptions,
    profile_id: connection_id as string
  });

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    setSearchParams({ "tab": currentTab }, { replace: true })

  }, [currentTab, setSearchParams]);

  const onChangeJobsPage = useCallback((_event: any, pageNumber: number) => {
    setJobsPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeJobsRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setJobsPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const onChangeFilters = useCallback((name: keyof JobFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onClearFilters = useCallback(() => {
    setFilters({
      search_query: undefined,
      job_status: undefined,
      assigned_account: undefined,
    });
  }, []);

  const onChangeApplicationsPage = useCallback((pageNumber: number) => {
    setApplicationsPageOptions((prev) => ({
      ...prev,
      page: pageNumber,
    }));
  }, []);

  const onChangeApplicationsRowsPerPage = useCallback((perPage: number) => {
    setApplicationsPageOptions((prev) => ({
      ...prev,
      per_page: perPage,
    }));
  }, []);

  const onRatingChange = useCallback((id: string, rating: number) => {
    dispatch(rootApi.util.invalidateTags([{ type: 'CompanyApplications', id: connection_id as string }]));
  }, [dispatch, connection_id]);

  const handleAddTask = useCallback(() => {
    dispatch(openTaskEditorSidesheet({
      companyId: connection_id,
    }))
  }, [dispatch, connection_id]);

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={
          <Stack direction="row" justifyContent="center" alignItems="center">
            <TruncatedTextField
              text={connectionProfile?.company_name ?? 'Loading...'}
              limit={40}
              mode='characters'
              variant="h3"
              showMoreEnabled={false}
            />

          </Stack>
        }
        avatar={
          connectionProfile?.company_logo && (
            <Box height="100%" paddingTop={1}>
              <Avatar src={connectionProfile?.company_logo.public_path}>
                {connectionProfile?.company_name.charAt(0).toUpperCase()}
              </Avatar>
            </Box>
          )
        }
        action={
          <Stack direction="row" spacing={.5} alignItems="center" mt={2}>

            <CompanyProfileTypeIcon type={connectionProfile?.type} />

            {
              connectionProfile?.company_type && (
                <Label variant="soft">{t(`connections.table.company_type.${connectionProfile?.company_type}`)}</Label>
              )
            }

          </Stack>
        }
      />
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>

      <Tab
        key={CompaniesTab.DETAIL}
        iconPosition="end"
        value={CompaniesTab.DETAIL}
        label={t('connections.detail.tabs.detail')}
      />

      <Tab
        key={CompaniesTab.PROFILE}
        iconPosition="end"
        value={CompaniesTab.PROFILE}
        label={t('connections.detail.tabs.profile')}
      />

      <Tab
        key={CompaniesTab.JOBS}
        iconPosition="end"
        value={CompaniesTab.JOBS}
        label={t('connections.detail.tabs.jobs')}
        icon={<Label variant="filled">{connectionJobs?.count ?? 0}</Label>}
      />

      {tenantType !== TenantType.Candidate && (
        <Tab
          key={CompaniesTab.APPLICATIONS}
          iconPosition="end"
          value={CompaniesTab.APPLICATIONS}
          label={t(
            `connections.detail.tabs.${isOrganisationTenant(tenantType) ? 'applications' : 'applications_candidate'
            }`
          )}
          icon={<Label variant="filled">{connectionApplications?.count ?? 0}</Label>}
        />
      )}

      <Tab
        key={CompaniesTab.CONTACTS}
        value={CompaniesTab.CONTACTS}
        iconPosition="end"
        label={t('connections.detail.tabs.contacts')}
        icon={<Label variant="filled">{contactLinks?.count ?? 0}</Label>}
      />

      <Tab
        key={CompaniesTab.DOCUMENTS}
        value={CompaniesTab.DOCUMENTS}
        iconPosition="end"
        label={t('connections.detail.tabs.documents')}
      />

    </Tabs>
  );

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      {renderTabs}

      <Grid container spacing={2}>
        <Grid xs={12} md={8}>
          {currentTab === CompaniesTab.DETAIL && (
            <CompanyDetailSection companyProfileId={connection_id as string} />
          )}

          {currentTab === CompaniesTab.PROFILE && (
            <CompanyProfileSection companyProfileId={connection_id as string} />
          )}

          {currentTab === CompaniesTab.JOBS && (
            <ConnectionJobsTable
              jobs={connectionJobs}
              pageOptions={jobsPageOptions}
              onChangePage={onChangeJobsPage}
              onChangeRowsPerPage={onChangeJobsRowsPerPage}
              showTable={showTable}
              filters={filters}
              onChangeFilters={onChangeFilters}
              onClearFilters={onClearFilters}
            />
          )}

          {currentTab === CompaniesTab.APPLICATIONS && (
            <ApplicationsTable
              loading={isLoadingApplications}
              tableData={connectionApplications}
              pageOptions={applicationsPageOptions}
              onChangePage={onChangeApplicationsPage}
              onChangeRowsPerPage={onChangeApplicationsRowsPerPage}
              onRatingChange={onRatingChange}
            />
          )}

          {currentTab === CompaniesTab.CONTACTS && (
            <ContactLinksTable
              profileId={connection_id as string}
            />
          )}

          {currentTab === CompaniesTab.DOCUMENTS && (
            <CompanyDocumentsView profileId={connection_id as string} />
          )}
        </Grid>

        <Grid xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            <Stack direction={{ xs: "column" }} spacing={2} width="100%" justifyContent="center" alignItems="center">
              <Box width="100%" >
                <AccountOwnerCard profileId={connection_id as string} />
              </Box>

              <Box width="100%" >
                <PrimaryContactCard profileId={connection_id as string} />
              </Box>
            </Stack>

            <Divider sx={{ borderStyle: 'dashed' }} />

            <LinkedContactsCard entityId={connection_id as string} entityType={ContactLinkEntityType.COMPANY_PROFILE} enableTypeSelection />

            <CompanyLinksCard profileId={connection_id as string} />

            <CompanyLabelsCard companyProfileId={connection_id as string} />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
